import React, { useEffect, useState } from 'react';
import { useInventoryStore } from '../../stores/inventoryStore';
import { useGameStore } from '../../stores/gameStore';
import { EquipmentSlot } from '@hexcrawl/shared-utils';
import type { IItemInstanceDTO } from '@hexcrawl/shared-utils';
import { abilityService } from '../../services/abilityService';
import { ItemIcon } from './ItemIcon';
import { StackBadge } from './StackBadge';
import { EquipmentPanel, type EquippedItems } from './EquipmentPanel';
import { GrantCheatSection } from './GrantCheatSection';
import { useCheatsEnabled } from '../../stores/cheatStore';
import { consumableContextLabel, isUsableOnMap, needsHexTarget, summarizeConsumable } from '../../utils/consumableSummary';
import { useUiStore } from '../../stores/uiStore';

// How long the "you drank it" line stays in the info area before the panel reverts
// to its normal hover behavior.
const USE_MESSAGE_DURATION_MS = 4000;

/** Dropping a cell drops the whole stack, so the confirmation names every unit. */
function dropLabel(item: IItemInstanceDTO): string {
  return item.quantity > 1 ? `all ${item.quantity} × ${item.def.name}` : item.def.name;
}

interface InventoryPanelProps {
  /**
   * Show the amber Grant cheat section at the foot of the panel (still subject to the
   * cheat toggle). Off by default so the copy embedded in the loot overlay stays clean —
   * rolling a new table there would replace the loot being reviewed.
   */
  showGrantCheat?: boolean;
}

type HoveredTarget =
  | { kind: 'inventory'; instanceId: string }
  | { kind: 'equipment'; slot: EquipmentSlot }
  | null;

/**
 * Destructive and irreversible bag actions are two-click: the first click on the
 * button arms it, the second one commits. Only one action is ever armed at a time.
 */
export type BagActionKind = 'drop' | 'use';

interface PendingAction {
  instanceId: string;
  kind: BagActionKind;
}

export const InventoryPanel: React.FC<InventoryPanelProps> = ({ showGrantCheat = false }) => {
  const inventory = useInventoryStore((s) => s.inventory);
  const equipment = useInventoryStore((s) => s.equipment);
  const dropItem = useInventoryStore((s) => s.dropItem);
  const equipItem = useInventoryStore((s) => s.equipItem);
  const unequipItem = useInventoryStore((s) => s.unequipItem);
  const useItem = useInventoryStore((s) => s.useItem);
  const lastUse = useInventoryStore((s) => s.lastUse);
  const clearLastUse = useInventoryStore((s) => s.clearLastUse);
  const characterId = useGameStore((s) => s.gameState?.character?.id);
  const gameId = useGameStore((s) => s.gameState?.id);
  const inventoryCapacity = useGameStore((s) => s.gameConfig?.inventoryCapacity ?? 0);
  const cheatsEnabled = useCheatsEnabled();

  const [hovered, setHovered] = useState<HoveredTarget>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abilityNames, setAbilityNames] = useState<Map<string, string>>(new Map());

  const itemsById = new Map(inventory.map(i => [i.id, i]));
  const equippedIds = new Set(Object.values(equipment).filter((v): v is string => !!v));
  const equippedItems: EquippedItems = {};
  for (const [slot, id] of Object.entries(equipment)) {
    if (id) equippedItems[slot as EquipmentSlot] = itemsById.get(id);
  }
  // Each unequipped instance is one bag cell — a stack is a single instance carrying
  // its own quantity, so the grid needs no grouping of its own.
  const unequipped = inventory.filter(i => !equippedIds.has(i.id));

  // Preload ability names for all items with grantedAbilities
  useEffect(() => {
    const ids = [...new Set(inventory.flatMap(i => i.def.grantedAbilities))];
    if (ids.length === 0) return;
    Promise.all(ids.map(id => abilityService.getAbility(id).then(a => [id, a.name] as const).catch(() => [id, id] as const)))
      .then(entries => setAbilityNames(new Map(entries)));
  }, [inventory]);

  // The use message is transient — it would otherwise sit in the info area forever,
  // since the mouse is still hovering the cell that was just clicked.
  useEffect(() => {
    if (!lastUse) return;
    const timer = setTimeout(() => clearLastUse(), USE_MESSAGE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [lastUse, clearLastUse]);

  const showError = (msg: string) => {
    setError(msg);
    setTimeout(() => setError(null), 3000);
  };

  /**
   * Clicking the cell body equips wearables. It never uses or drops anything — those
   * live on their own hover buttons — and it always cancels a pending confirmation,
   * so a stray click on an armed item is a way out rather than a way to commit.
   */
  const handleCellClick = async (item: IItemInstanceDTO) => {
    if (pending) {
      const wasThisItem = pending.instanceId === item.id;
      setPending(null);
      if (wasThisItem) return;
    }
    if (item.def.consumable) return;
    return handleEquip(item);
  };

  const handleUseClick = async (item: IItemInstanceDTO) => {
    if (!gameId) return;
    if (!isUsableOnMap(item.def)) return;

    // An item that needs a destination is not spent here: picking the hex is itself the
    // confirming second click, so it hands off to the map's targeting mode instead.
    if (needsHexTarget(item.def)) {
      setPending(null);
      clearLastUse();
      useUiStore.getState().startHexTargeting({ instanceId: item.id, itemName: item.def.name });
      return;
    }

    if (pending?.kind !== 'use' || pending.instanceId !== item.id) {
      setPending({ instanceId: item.id, kind: 'use' });
      return;
    }
    setPending(null);
    clearLastUse();
    try {
      await useItem(gameId, item.id);
      // The cell is gone if that was the stack's last unit, so nothing is hovered now.
      setHovered(null);
    } catch (err: any) {
      showError(err.message || 'Failed to use item.');
    }
  };

  const handleEquip = async (item: IItemInstanceDTO) => {
    if (!characterId) return;
    if (item.def.slot === EquipmentSlot.NONE) return;
    try {
      await equipItem(characterId, item.id, item.def.slot);
    } catch (err: any) {
      showError(err.message || 'Failed to equip item.');
    }
  };

  const handleUnequip = async (slot: EquipmentSlot) => {
    if (!characterId) return;
    try {
      await unequipItem(characterId, slot);
    } catch (err: any) {
      showError(err.message || 'Failed to unequip item.');
    }
  };

  const handleDropClick = async (instanceId: string) => {
    if (!characterId) return;
    if (pending?.kind !== 'drop' || pending.instanceId !== instanceId) {
      setPending({ instanceId, kind: 'drop' });
      return;
    }
    setPending(null);
    try {
      await dropItem(characterId, instanceId);
      setHovered(null);
    } catch (err: any) {
      showError(err.message || 'Failed to drop item.');
    }
  };

  // Determine what to show in the info area
  const hoveredInventoryItem = hovered?.kind === 'inventory'
    ? inventory.find(i => i.id === hovered.instanceId) ?? null
    : null;
  const hoveredEquipmentItem = hovered?.kind === 'equipment'
    ? (equippedItems[hovered.slot] ?? null)
    : null;
  const tooltipItem = hoveredInventoryItem ?? hoveredEquipmentItem;
  const pendingItem = pending ? inventory.find(i => i.id === pending.instanceId) ?? null : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', color: '#a8d1ff', fontFamily: 'monospace', flex: 1, minHeight: 0 }}>
      <div className="inv-panel-body">
        {/* Equipment slots */}
        <div className="inv-equip-section">
          <div style={{ fontSize: 11, color: '#7799bb', marginBottom: 6 }}>Equipped</div>
          <EquipmentPanel
            items={equippedItems}
            onUnequip={handleUnequip}
            hoveredSlot={hovered?.kind === 'equipment' ? hovered.slot : null}
            onSlotMouseEnter={(slot) => setHovered({ kind: 'equipment', slot })}
            onSlotMouseLeave={() => setHovered(null)}
          />
        </div>

        <div className="inv-divider" />

        {/* Bag grid */}
        <div className="inv-bag-section">
          <div style={{ fontSize: 11, color: '#7799bb', marginBottom: 6 }}>
            Bag <span style={{ color: '#556' }}>{unequipped.length} / {inventoryCapacity}</span>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 2 }}>
              {unequipped.length === 0 && (
                <span style={{ color: '#4a6a8a', fontSize: 13 }}>No items</span>
              )}
              {unequipped.map((item) => (
                <ItemCell
                  key={item.id}
                  item={item}
                  hovered={hovered?.kind === 'inventory' && hovered.instanceId === item.id}
                  pendingKind={pending?.instanceId === item.id ? pending.kind : null}
                  onMouseEnter={() => setHovered({ kind: 'inventory', instanceId: item.id })}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => handleCellClick(item)}
                  onUseClick={() => handleUseClick(item)}
                  onDropClick={() => handleDropClick(item.id)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Info area — always at bottom, fixed height so it never shifts layout */}
      <div style={{
        padding: '8px 10px',
        background: 'rgba(20, 40, 60, 0.95)',
        border: '1px solid #3a6a9a',
        borderRadius: 6,
        fontSize: 12,
        height: 56,
        marginTop: 12,
        boxSizing: 'border-box',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {error ? (
          <div style={{ color: '#ff6666' }}>{error}</div>
        ) : pending && pendingItem ? (
          <div style={{ color: '#ffaa44' }}>
            {pending.kind === 'drop'
              ? `Click the ✕ button again to drop ${dropLabel(pendingItem)}.`
              : `Click the Use button again to use ${pendingItem.def.name}.`}
            <div style={{ color: '#8a7a5a', marginTop: 2 }}>Click the item to cancel.</div>
          </div>
        ) : lastUse ? (
          <>
            <div style={{ marginBottom: 3, color: '#e0f0ff' }}>{lastUse.useText}</div>
            <div style={{ color: '#aaddaa' }}>
              {lastUse.appliedEffects.map(e => e.message).join(' · ')}
            </div>
          </>
        ) : tooltipItem ? (
          <>
            <div style={{ fontWeight: 'bold', marginBottom: 3, color: '#e0f0ff' }}>{tooltipItem.def.name}</div>
            <div style={{ color: '#8ab0d0' }}>
              {tooltipItem.def.description}
              {tooltipItem.def.consumable && (
                <span style={{ color: '#aaddaa', marginLeft: 6 }}>
                  · {summarizeConsumable(tooltipItem.def.consumable)}
                </span>
              )}
              {tooltipItem.def.grantedAbilities.length > 0 && (
                <span style={{ color: '#aaddaa', marginLeft: 6 }}>
                  · Grants: {tooltipItem.def.grantedAbilities.map(id => abilityNames.get(id) ?? id).join(', ')}
                </span>
              )}
            </div>
          </>
        ) : (
          <div style={{ color: '#3a5a7a' }}>
            Click an item to equip it, or hover it for Use and Drop buttons.
            Click an equipped slot to unequip.
          </div>
        )}
      </div>

      {showGrantCheat && cheatsEnabled && <GrantCheatSection />}
    </div>
  );
};

const ItemCell: React.FC<{
  item: IItemInstanceDTO;
  hovered: boolean;
  /** Which of this cell's buttons is armed and awaiting its confirming second click. */
  pendingKind: BagActionKind | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onUseClick: () => void;
  onDropClick: () => void;
}> = ({ item, hovered, pendingKind, onMouseEnter, onMouseLeave, onClick, onUseClick, onDropClick }) => {
  const consumable = item.def.consumable;
  // Combat-only consumables stay visible but inert here, so the player can see what
  // they are carrying without being able to waste it on the map.
  const unusableHere = !!consumable && !isUsableOnMap(item.def);
  const contextLabel = consumable ? consumableContextLabel(consumable) : null;
  const equippable = !consumable && item.def.slot !== EquipmentSlot.NONE;
  // A hex-targeted item is armed by the map, not by a second click here.
  const hexTargeted = !!consumable && needsHexTarget(item.def);
  const pendingDrop = pendingKind === 'drop';
  const pendingUse = pendingKind === 'use';
  // The buttons stay up while armed, so the confirming click is always reachable
  // even if the pointer has drifted off the cell.
  const showButtons = hovered || pendingKind !== null;

  return (
  <div
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    style={{
      position: 'relative',
      width: 64,
      height: 80,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      padding: 4,
      background: pendingDrop ? 'rgba(255, 80, 80, 0.15)' : pendingUse ? 'rgba(80, 255, 120, 0.12)' : item.def.isQuestItem ? 'rgba(80, 60, 10, 0.5)' : hovered ? 'rgba(90, 160, 255, 0.15)' : 'rgba(20, 40, 60, 0.7)',
      border: pendingDrop ? '1px solid #ff6666' : pendingUse ? '1px solid #55cc77' : item.def.isQuestItem ? '1px solid #cc9933' : hovered ? '1px solid #5aa0ff' : '1px solid #2a4a6a',
      borderRadius: 6,
      cursor: equippable ? 'pointer' : 'default',
      opacity: unusableHere ? 0.45 : 1,
    }}
  >
    <ItemIcon atlas={item.def.icon.atlas} frame={item.def.icon.frame} size={48} />
    <span style={{ fontSize: 9, color: '#8ab0d0', textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}>
      {item.def.name}
    </span>

    {/* Stack count — a stackable def is one row however many units it holds */}
    <StackBadge quantity={item.quantity} />

    {/* Why this consumable can't be clicked from here */}
    {unusableHere && contextLabel && (
      <span style={{
        position: 'absolute',
        top: 2,
        left: 3,
        color: '#ddaa55',
        fontSize: 8,
        lineHeight: '10px',
      }}>
        {contextLabel}
      </span>
    )}

    {/* Drop button — hidden for items that cannot be dropped */}
    {showButtons && !item.def.isQuestItem && (
      <button
        onClick={(e) => { e.stopPropagation(); onDropClick(); }}
        title={pendingDrop ? 'Click again to confirm drop' : 'Drop item'}
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          width: 16,
          height: 16,
          padding: 0,
          background: pendingDrop ? '#cc2222' : 'rgba(60, 20, 20, 0.85)',
          border: '1px solid #ff6666',
          borderRadius: 3,
          color: pendingDrop ? '#ffffff' : '#ffaaaa',
          fontSize: 10,
          lineHeight: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>
    )}

    {/* Use button — consumables only, and only where this one can actually be used */}
    {showButtons && consumable && !unusableHere && (
      <button
        onClick={(e) => { e.stopPropagation(); onUseClick(); }}
        title={hexTargeted ? 'Pick a hex to travel to' : pendingUse ? 'Click again to confirm use' : 'Use item'}
        style={{
          position: 'absolute',
          bottom: 2,
          right: 2,
          padding: '0 4px',
          height: 16,
          background: pendingUse ? '#22aa55' : 'rgba(20, 60, 30, 0.9)',
          border: '1px solid #55cc77',
          borderRadius: 3,
          color: pendingUse ? '#ffffff' : '#aaeebb',
          fontSize: 9,
          lineHeight: '14px',
          fontFamily: 'monospace',
          cursor: 'pointer',
        }}
      >
        {pendingUse ? 'Use?' : 'Use'}
      </button>
    )}
  </div>
  );
};
